import type { Fraction } from './fractionType';
import { approximateFractionValue, simplifyFraction } from './integerMath';
import { safeArrayAccess } from './safeArrayAccess';

export const millisecondToSecond = (milliSeconds: number) => {
  return milliSeconds / 1000;
};

export function formatHmsmTime(totalSecs: number) {
  let sign = '';
  if (totalSecs < 0) {
    totalSecs = -totalSecs;
    sign = '-';
  }
  const totalMins = Math.floor(totalSecs / 60);
  const secs = totalSecs - totalMins * 60;
  const totalHours = Math.floor(totalMins / 60);
  const mins = totalMins - totalHours * 60;
  return sign + (totalHours ? totalHours + 'h' : '') + (totalMins ? mins + 'm' : '') + secs.toFixed(3) + 's';
}

/**
 * Parse a duration string in hmsm format back to seconds.
 * Accepts formats like: "5h32m6.123s", "32m6s", "6.5s", "-1h30m0s"
 * Returns undefined if the string is not a valid duration.
 */
export function parseHmsmTime(str: string): number | undefined {
  const trimmed = str.trim();
  if (trimmed === '') {
    return undefined;
  }

  let remaining = trimmed;
  let sign = 1;

  if (remaining.startsWith('-')) {
    sign = -1;
    remaining = remaining.slice(1);
  }

  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  let hasAnyUnit = false;

  // Parse hours
  const hoursMatch = /^(\d+(?:\.\d+)?)h/.exec(remaining);
  if (hoursMatch) {
    hours = parseFloat(safeArrayAccess(hoursMatch, 1));
    remaining = remaining.slice(hoursMatch[0].length);
    hasAnyUnit = true;
  }

  // Parse minutes
  const minutesMatch = /^(\d+(?:\.\d+)?)m/.exec(remaining);
  if (minutesMatch) {
    minutes = parseFloat(safeArrayAccess(minutesMatch, 1));
    remaining = remaining.slice(minutesMatch[0].length);
    hasAnyUnit = true;
  }

  // Parse seconds
  const secondsMatch = /^(\d+(?:\.\d+)?)s/.exec(remaining);
  if (secondsMatch) {
    seconds = parseFloat(safeArrayAccess(secondsMatch, 1));
    remaining = remaining.slice(secondsMatch[0].length);
    hasAnyUnit = true;
  }

  // If there's remaining content or no units were parsed, it's invalid
  if (remaining !== '' || !hasAnyUnit) {
    return undefined;
  }

  return sign * (hours * 3600 + minutes * 60 + seconds);
}

/**
 * Make string from duration expressed as fraction, suitable for diagnostic
 * messages.
 */
export function formatDurationFraction(frac: Fraction) {
  const simple = simplifyFraction(frac);
  return simple.numerator + (simple.denominator == 1 ? '' : '/' + simple.denominator) + ' (' + formatHmsmTime(approximateFractionValue(frac)) + ')';
}
